/**
 * logURLEvent — fire-and-forget audit event emitter for URL directory activities.
 *
 * All events are piped to POST /api/telemetry/url-events for admin stream ingestion.
 * Events: NAVIGATION_DEEP_LINK, LAYOUT_SHARE_HYDRATE, DUAL_KEY_TRIGGER, COPILOT_TIER_ACCESS
 */
import { api } from "@/lib/api";

/**
 * @typedef {'NAVIGATION_DEEP_LINK' | 'LAYOUT_SHARE_HYDRATE' | 'DUAL_KEY_TRIGGER' | 'COPILOT_TIER_ACCESS'} URLEventType
 */

/**
 * Emit a URL activity log event to the backend.
 * Fire-and-forget — never throws, never blocks UI.
 *
 * @param {URLEventType} eventType
 * @param {string} canonicalPath
 * @param {Record<string, string>} [queryParams]
 * @param {Record<string, any>} [extra]
 */
export function logURLEvent(eventType, canonicalPath, queryParams = {}, extra = {}) {
    const payload = {
        event_type: eventType,
        canonical_path: canonicalPath,
        query_params: queryParams,
        timestamp: new Date().toISOString(),
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        ...extra,
    };

    // Fire-and-forget — silent on failure
    api.post("/telemetry/url-events", payload).catch(() => {});
}

/**
 * Parse current URL search params into a plain object.
 * @returns {Record<string, string>}
 */
export function parseSearchParams(search) {
    const params = new URLSearchParams(search);
    const obj = {};
    for (const [k, v] of params.entries()) {
        obj[k] = v;
    }
    return obj;
}
